import React, { useMemo, useRef, useState } from "react";
import { ClipboardList, Loader2, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Cluster 11 — GC punchlist upload. Parse → review (ABS rows pre-checked,
 * room/scope proposals editable) → Apply. Nothing is written until Apply.
 */
export default function PunchUploadDialog({ open, onOpenChange, projectId, rooms, scopes, onApplied }) {
  const [parsed, setParsed] = useState(null);   // { upload_id, rows, total, ours }
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const roomOptions = useMemo(() => {
    const seen = new Map();
    for (const r of rooms || []) {
      const key = [r.building || "", r.floor || "", r.area || "", r.room_number || "", r.room_type || ""].join("||");
      if (!seen.has(key)) seen.set(key, r);
    }
    return [...seen.entries()];
  }, [rooms]);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/projects/${projectId}/punchlist/parse`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setParsed(data);
      setRows(data.rows);
      toast.success(`${data.total} rows read — ${data.ours} look like ours`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not read that punchlist");
    }
    setBusy(false);
  };

  const setRow = (idx, patch) =>
    setRows((cur) => cur.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const apply = async () => {
    const keep = rows.filter((r) => r.keep);
    if (keep.length === 0) { toast.error("Tick at least one row to import"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/punchlist/apply`, {
        upload_id: parsed.upload_id,
        rows: keep.map((r) => ({
          source_key: r.source_key, description: r.description, location_text: r.location_text,
          responsible: r.responsible, gc_status: r.gc_status, note: r.note,
          building: r.building, floor: r.floor, area: r.area,
          room_number: r.room_number, room_type: r.room_type, scope_type: r.scope_type,
        })),
      });
      toast.success(`${data.created} new punch item${data.created === 1 ? "" : "s"}`, {
        description: `${data.updated} updated · ${data.closed_by_gc} closed by GC`,
      });
      onApplied && onApplied();
      setParsed(null);
      setRows([]);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not apply the punchlist");
    }
    setBusy(false);
  };

  const keptCount = rows.filter((r) => r.keep).length;
  // Every company scope code is selectable — punch work often belongs to a
  // scope that is not on this project's FRS yet.
  const scopeOptions = useMemo(() => [...new Set([
    ...(parsed?.scope_options || []), ...(scopes || []),
    ...rows.map((r) => r.scope_type).filter(Boolean),
  ])].sort(), [parsed, scopes, rows]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setParsed(null); setRows([]); } onOpenChange(o); }}>
      <DialogContent className="max-w-6xl p-0 overflow-hidden max-h-[92vh] flex flex-col" data-testid="punch-upload-dialog">
        <DialogHeader className="px-4 py-3 border-b border-gray-200 bg-amber-50">
          <DialogTitle className="text-base font-bold text-gray-900 flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-amber-700" /> Upload punchlist
          </DialogTitle>
          <div className="text-[11px] text-gray-600">
            XLSX, CSV or a text-based PDF. Rows assigned to us are pre-checked — nothing saves until you Apply.
          </div>
        </DialogHeader>

        <div className="px-4 py-3 overflow-auto flex-1">
          {!parsed ? (
            <div className="py-10 text-center">
              <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.csv,.txt,.pdf" onChange={upload}
                     className="hidden" data-testid="punch-file-input" />
              <Button onClick={() => fileRef.current?.click()} disabled={busy}
                      className="h-11 bg-blue-600 hover:bg-blue-700 text-white" data-testid="punch-pick-file-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Choose punchlist file
              </Button>
              <div className="text-[11px] text-gray-500 mt-2">
                Responsible-party matching uses the synonyms list in Settings.
              </div>
            </div>
          ) : (
            <table className="w-full text-[12px]" data-testid="punch-review-table">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-2 py-1 text-left w-8">Keep</th>
                  <th className="px-2 py-1 text-left">Item</th>
                  <th className="px-2 py-1 text-left">Location</th>
                  <th className="px-2 py-1 text-left">Trade</th>
                  <th className="px-2 py-1 text-left">Room match</th>
                  <th className="px-2 py-1 text-left">Scope</th>
                  <th className="px-2 py-1 text-left">GC note</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.source_key + i} className={`border-t border-gray-100 ${r.keep ? "bg-white" : "bg-gray-50/60 text-gray-500"}`}
                      data-testid={`punch-review-row-${i}`}>
                    <td className="px-2 py-1.5">
                      <input type="checkbox" className="w-4 h-4" checked={!!r.keep}
                             onChange={() => setRow(i, { keep: !r.keep })}
                             data-testid={`punch-keep-${i}`} />
                    </td>
                    <td className="px-2 py-1.5 max-w-[260px]">
                      <div className="truncate" title={r.description}>{r.description}</div>
                      {r.already_known && <span className="text-[10px] text-blue-700">already imported</span>}
                    </td>
                    <td className="px-2 py-1.5 max-w-[180px] truncate" title={r.location_text}>{r.location_text || "—"}</td>
                    <td className="px-2 py-1.5">
                      <span className={r.is_ours ? "font-semibold text-emerald-700" : ""}>{r.responsible || "—"}</span>
                    </td>
                    <td className="px-2 py-1.5">
                      <select
                        value={[r.building, r.floor, r.area, r.room_number, r.room_type].join("||")}
                        onChange={(e) => {
                          const [building, floor, area, room_number, room_type] = e.target.value.split("||");
                          setRow(i, { building, floor, area, room_number, room_type });
                        }}
                        className="h-8 text-[12px] border border-gray-300 rounded px-1 bg-white max-w-[190px]"
                        data-testid={`punch-room-${i}`}
                      >
                        <option value="||||">— no room —</option>
                        {roomOptions.map(([key, r2]) => (
                          <option key={key} value={key}>
                            {[r2.building, r2.floor && `F${r2.floor}`, r2.room_number, r2.room_name].filter(Boolean).join(" · ")}
                          </option>
                        ))}
                      </select>
                      {r.room_confidence > 0 && (
                        <div className="text-[10px] text-gray-400">match {Math.round(r.room_confidence * 100)}%</div>
                      )}
                    </td>
                    <td className="px-2 py-1.5">
                      <select
                        value={r.scope_type || ""}
                        onChange={(e) => setRow(i, { scope_type: e.target.value })}
                        className="h-8 text-[12px] border border-gray-300 rounded px-1 bg-white"
                        data-testid={`punch-scope-${i}`}
                      >
                        <option value="">— none —</option>
                        {scopeOptions.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 py-1.5 max-w-[200px] truncate" title={r.note}>{r.note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {parsed && (
          <DialogFooter className="px-4 py-3 border-t border-gray-200 bg-gray-50">
            <span className="text-xs text-gray-600 mr-auto" data-testid="punch-keep-count">
              {keptCount} of {rows.length} rows will be imported
            </span>
            <Button variant="outline" className="h-10" onClick={() => { setParsed(null); setRows([]); }}
                    data-testid="punch-restart-btn">
              Choose another file
            </Button>
            <Button className="h-10 bg-blue-600 hover:bg-blue-700 text-white" onClick={apply} disabled={busy}
                    data-testid="punch-apply-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Apply {keptCount} item{keptCount === 1 ? "" : "s"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
